import { createContext, useContext, useEffect } from "react";
import { get, isEqual, isFunction } from "lodash";
import { useFormContext } from "react-hook-form";
import { usePunditUserContext } from "@circle-react/contexts";
import { useReducerWithThunk } from "@circle-react/hooks/useReducerWithThunk";
import type { RecordType, SelectionState, Value, Variant } from "../types";
import { add, fetchInitialRecords, remove, reset } from "./selectionActions";
import { initialState, selectionReducer } from "./selectionReducer";

const CommunityMemberPickerContext = createContext<
  CommunityMemberPickerContextValue | undefined
>(undefined);
CommunityMemberPickerContext.displayName = "CommunityMemberPickerContext";

interface CommunityMemberPickerContextValue {
  excludeCommunityMemberIds: number[];
  includeAllResultTypes: boolean;
  onAdd: (record: RecordType) => void;
  onRemove: (record: RecordType) => void;
  onSave: () => void;
  selectionState: SelectionState;
}

interface CommunityMemberPickerProviderProps {
  allowCurrentCommunityMember?: boolean;
  children: React.ReactNode;
  excludedMemberIds?: number[];
  includeAllResultTypes?: boolean;
  name?: string;
  onChange: (value: Value) => void;
  onModalSave?: (value: Value) => void;
  value: Value;
  variant?: Variant;
}

export const useCommunityMemberPickerContext =
  (): CommunityMemberPickerContextValue => {
    const context = useContext(CommunityMemberPickerContext);
    if (!context) {
      throw new Error(
        "useCommunityMemberPickerContext must be used within a CommunityMemberPickerProvider",
      );
    }
    return context;
  };

export const CommunityMemberPickerProvider = ({
  onChange,
  value,
  name,
  includeAllResultTypes = false,
  excludedMemberIds = [],
  allowCurrentCommunityMember = false,
  children,
  variant = "simple",
  onModalSave,
}: CommunityMemberPickerProviderProps) => {
  const { currentCommunityMember } = usePunditUserContext() || {};
  const [selectionState, dispatch] = useReducerWithThunk(
    selectionReducer,
    initialState(value || [], includeAllResultTypes),
  );

  const { getValues } = useFormContext<{ [key: string]: Value }>();

  const excludeCommunityMemberIds = [
    allowCurrentCommunityMember ? undefined : currentCommunityMember?.id,
    ...excludedMemberIds,
    ...selectionState.communityMembers.map(
      communityMember => communityMember.id,
    ),
  ].filter((id): id is number => id !== undefined);

  useEffect(() => {
    async function fetchInitial() {
      await fetchInitialRecords(value, includeAllResultTypes)(dispatch);
    }
    void fetchInitial();
  }, []);

  // Mechanism for resetting the value by setting null from the form
  useEffect(() => {
    if (!value) {
      dispatch(reset(includeAllResultTypes));
    }
  }, [dispatch, includeAllResultTypes, value]);

  useEffect(() => {
    const currentValue = get(getValues(), name || "", null);
    if (variant === "simple" && !isEqual(selectionState.value, currentValue)) {
      onChange(selectionState.value);
    }
  }, [selectionState.value, variant, getValues, name, onChange]);

  const onAdd = (record: RecordType) =>
    dispatch(add(record, includeAllResultTypes, true));

  const onRemove = (record: RecordType) => {
    dispatch(remove(record, includeAllResultTypes));
  };

  const onSave = () => {
    if (!isEqual(selectionState.value, value)) {
      onChange(selectionState.value);
    }
    if (isFunction(onModalSave)) {
      onModalSave(selectionState.value);
    }
  };

  const contextValue: CommunityMemberPickerContextValue = {
    selectionState,
    onAdd,
    onRemove,
    onSave,
    excludeCommunityMemberIds,
    includeAllResultTypes,
  };

  return (
    <CommunityMemberPickerContext.Provider value={contextValue}>
      {children}
    </CommunityMemberPickerContext.Provider>
  );
};
